import { defineListener, resourceListTag, type ModuleListener } from '@krp/client-runtime'

import { AQSI_TAGS } from '../../../shared/api/tags'
import { storeDispatch, type AqsiDispatch } from '../../../shared/model/dispatch'
import { feedbackOfError } from '../../../shared/model/feedback'
import { FEEDBACK_SCOPES, feedbackSet } from '../../../shared/model/feedbackSlice'
import { ridKey } from '../../../shared/model/keys'
import { aqsiCatalogRefresh, aqsiDevicesRefresh, aqsiFiscalCheck, catalogApi } from '../api/catalogApi'
import type { AqsiRefreshResult } from '../model/types'
import {
  catalogRefreshRequested,
  catalogReloadRequested,
  devicesRefreshRequested,
  fiscalCheckCacheKey,
  fiscalCheckRequested,
} from './commands'

function refreshNote(what: string, result: AqsiRefreshResult): string {
  return (
    `${what}: прочитано ${result.seen}, новых ${result.added}, изменено ${result.changed}, ` +
    `пропало из кабинета ${result.newlyMissing}, не по формату ${result.invalid}, без ключа ${result.unkeyed}`
  )
}

async function refresh(
  dispatch: AqsiDispatch,
  resourceId: string,
  command: string,
  what: string,
  send: () => Promise<AqsiRefreshResult>,
): Promise<void> {
  const key = ridKey(resourceId, FEEDBACK_SCOPES.catalog)
  dispatch(feedbackSet({ key, feedback: { command, target: resourceId, phase: 'sending', error: null, note: null } }))
  try {
    const result = await send()
    dispatch(
      feedbackSet({
        key,
        feedback: { command, target: resourceId, phase: 'done', error: null, note: refreshNote(what, result) },
      }),
    )
  } catch (error) {
    const result = feedbackOfError(command, resourceId, error)
    dispatch(
      feedbackSet({
        key,
        feedback:
          result.phase === 'unknown'
            ? {
                ...result,
                note: 'Ответ не получен. Обновление не повторяется — снимок перечитан, проверьте время снимка.',
              }
            : result,
      }),
    )
  }
}

export function createCatalogListeners<S>(): ModuleListener<S>[] {
  return [
    defineListener<S, ReturnType<typeof catalogRefreshRequested>>(
      catalogRefreshRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const { resourceId } = action.payload
        await refresh(dispatch, resourceId, 'catalog.refresh', 'Каталог', () =>
          dispatch(aqsiCatalogRefresh.initiate({ resourceId }, { track: false })).unwrap(),
        )
      },
    ),
    defineListener<S, ReturnType<typeof devicesRefreshRequested>>(
      devicesRefreshRequested.match,
      async (action, api) => {
        const dispatch = storeDispatch(api.dispatch)
        const { resourceId } = action.payload
        await refresh(dispatch, resourceId, 'devices.refresh', 'Кассы и кассиры', () =>
          dispatch(aqsiDevicesRefresh.initiate({ resourceId }, { track: false })).unwrap(),
        )
      },
    ),
    defineListener<S, ReturnType<typeof fiscalCheckRequested>>(fiscalCheckRequested.match, async (action, api) => {
      const dispatch = storeDispatch(api.dispatch)
      const { resourceId } = action.payload
      try {
        await dispatch(
          aqsiFiscalCheck.initiate(action.payload, { fixedCacheKey: fiscalCheckCacheKey(resourceId) }),
        ).unwrap()
      } catch (error) {
        dispatch(
          feedbackSet({
            key: ridKey(resourceId, FEEDBACK_SCOPES.catalog),
            feedback: feedbackOfError('fiscal-profile.check', resourceId, error),
          }),
        )
      }
    }),
    defineListener<S, ReturnType<typeof catalogReloadRequested>>(catalogReloadRequested.match, (action, api) => {
      const { resourceId } = action.payload
      storeDispatch(api.dispatch)(
        catalogApi.util.invalidateTags([
          resourceListTag(AQSI_TAGS.goods, resourceId),
          resourceListTag(AQSI_TAGS.devices, resourceId),
          resourceListTag(AQSI_TAGS.cashiers, resourceId),
        ]),
      )
    }),
  ]
}
